import { create } from "zustand";

/**
 * Theme state — dark (default, the tested design) or light (additive `light:`
 * overrides; see tailwind.config.ts). Pure visual concern: this store knows
 * nothing about auth. Persistence layers:
 *   - localStorage `resto_theme` (every visitor, guests included) — read by
 *     the index.html bootstrap before first paint, so no flash.
 *   - profiles.theme (schema §26) for signed-in consumers — authStore hydrates
 *     it into here on profile load (DB wins) and writes it on toggle.
 *
 * Class discipline: <html> carries exactly one of `dark` | `light`. The token
 * palettes in globals.css flip on that class; hardcoded dark utilities are
 * overridden by `light:` variants (html.light scope), so dark stays untouched.
 */

export type Theme = "dark" | "light";

const STORAGE_KEY = "resto_theme";
const META_COLOR: Record<Theme, string> = { dark: "#0B0B0E", light: "#F5F1E8" };

export function readStoredTheme(): Theme {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark") return v;
  } catch {
    /* blocked storage → default */
  }
  return "dark";
}

function applyThemeToDocument(theme: Theme): void {
  const el = document.documentElement;
  el.classList.remove(theme === "dark" ? "light" : "dark");
  el.classList.add(theme);
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", META_COLOR[theme]);
  document.querySelector('meta[name="color-scheme"]')?.setAttribute("content", theme);
}

interface ThemeState {
  theme: Theme;
  /** Apply + persist to localStorage. DB write-through lives in authStore. */
  setTheme: (theme: Theme) => void;
}

export const useThemeStore = create<ThemeState>((set) => ({
  theme: readStoredTheme(),
  setTheme: (theme) => {
    set({ theme });
    applyThemeToDocument(theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* private mode — theme still applies for this page load */
    }
  },
}));

/** Boot-time apply (main.tsx, before render) — mirrors the index.html script. */
export function initTheme(): void {
  applyThemeToDocument(useThemeStore.getState().theme);
}
